import { EditOutlined } from "@ant-design/icons";
import { Alert, Button, Form, Input, Modal } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useUpdateDataConsumer,
  useUpdateDataConsumerContact,
} from "../../../api/api.data-consumers";
import { DataConsumerInformation } from "../../../screens/integration-platform/DataConsumerIntegration";

interface UpdateDataConsumerButtonProps {
  consumerId: string;
  dataConsumerInformation: DataConsumerInformation;
  refreshConsumer: () => void;
}

export function UpdateDataConsumerButton({
  consumerId,
  dataConsumerInformation,
  refreshConsumer,
}: UpdateDataConsumerButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  const updateDataConsumer = useUpdateDataConsumer(consumerId);
  const updateDataConsumerContact = useUpdateDataConsumerContact(consumerId);

  const onCancel = () => {
    if (isSaving) {
      return;
    }
    setIsOpen(false);
    setError(null);
    form.resetFields();
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);
      setError(null);

      const [consumerResult, contactResult] = await Promise.all([
        updateDataConsumer({ name: values.consumerName }),
        updateDataConsumerContact({
          id:
            dataConsumerInformation.contactId === ""
              ? {}
              : dataConsumerInformation.contactId,
          name: values.contactName,
          email: values.contactEmail,
        }),
      ]);

      if (consumerResult.error || contactResult.error) {
        throw new Error(t("translations:error-save"));
      }

      refreshConsumer();
      setIsOpen(false);
      form.resetFields();
    } catch (error) {
      setError(
        error instanceof Error ? error.message : t("translations:error")
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpen = () => {
    setIsOpen(true);
    form.setFieldsValue({
      consumerName: dataConsumerInformation.consumerName,
      contactName: dataConsumerInformation.contactName,
      contactEmail: dataConsumerInformation.contactEmail,
    });
  };

  const modalFooter = [
    <Button key="cancel" disabled={isSaving} onClick={onCancel}>
      {t("cancel")}
    </Button>,
    <Button
      key="save"
      disabled={isSaving}
      loading={isSaving}
      onClick={onSave}
      type="primary"
    >
      {t("save")}
    </Button>,
  ];

  return (
    <div data-testid="update-data-consumer-button">
      <Button onClick={handleOpen} type="primary">
        <EditOutlined /> {t("integrations:edit-data-consumer")}
      </Button>
      <Modal
        title={t("integrations:edit-data-consumer")}
        open={isOpen}
        onCancel={onCancel}
        footer={modalFooter}
        destroyOnClose
        data-testid="update-data-consumer-modal"
      >
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          requiredMark={false}
        >
          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              closable
              onClose={() => setError(null)}
              style={{ marginBottom: 16 }}
            />
          )}
          <Form.Item
            label={t("integrations:data-consumer-name")}
            name="consumerName"
            rules={[{ required: true, message: t("translations:required") }]}
          >
            <Input disabled={isSaving} />
          </Form.Item>
          <Form.Item
            label={`${t("integrations:contact-person")} ${t(
              "translations:name"
            )}`}
            name="contactName"
            rules={[{ required: true, message: t("translations:required") }]}
          >
            <Input disabled={isSaving} />
          </Form.Item>
          <Form.Item
            label={`${t("integrations:contact-person")} ${t("user:email")}`}
            name="contactEmail"
            rules={[{ required: true, message: t("translations:required") }]}
          >
            <Input disabled={isSaving} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
